import { useEffect, useMemo, useRef, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Popup,
  Tooltip,
} from "react-leaflet";
import L from "leaflet";
import { Maximize2 } from "lucide-react";
import "leaflet/dist/leaflet.css";
import VoyageCinema from "@/components/VoyageCinema";
import { VOYAGES, ALL_PORTS } from "@/data/voyages";
import {
  TRAVELS,
  BUSINESS_PLACES,
  LEISURE_PLACES,
  BUSINESS_COUNT_UNIQUE,
  LEISURE_COUNT_UNIQUE,
  CRUISES,
  CRUISE_PORTS,
  CRUISE_PORTS_UNIQUE,
} from "@/data/journey";
import { useTheme } from "@/hooks/useTheme";

// Four layer palette — Navy gold, Business deep blue, Leisure terracotta,
// Cruises sea-teal.
const LAYERS = {
  navy: { label: "Navy", color: "#d4af37", lightColor: "#8a6c1a" },
  business: { label: "Business", color: "#6aa3d6", lightColor: "#2e5d8a" },
  leisure: { label: "Leisure", color: "#e07a3c", lightColor: "#b85a1f" },
  cruises: { label: "Cruises", color: "#5ec4b6", lightColor: "#1f7a6e" },
};

const TILES = {
  dark: {
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
  },
  light: {
    url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
  },
};

// Inline SVG glyphs for the custom map pins (lucide outlines, simplified)
const PIN_SVGS = {
  navy:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="5" r="3"/><line x1="12" y1="22" x2="12" y2="8"/><path d="M5 12H2a10 10 0 0 0 20 0h-3"/></svg>',
  business:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>',
  leisure:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/></svg>',
  cruises:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76"/><path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6"/><path d="M12 10v4"/><path d="M12 2v3"/></svg>',
};

const pinIconCache = {};
function pinIcon(layer, color) {
  const key = `${layer}-${color}`;
  if (!pinIconCache[key]) {
    pinIconCache[key] = L.divIcon({
      className: "atlas-pin-wrap",
      html: `<span class="atlas-pin" style="--pin:${color}">${PIN_SVGS[layer]}</span>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });
  }
  return pinIconCache[key];
}

const NAVY_UNIQUE_PORTS = (() => {
  const seen = new Map();
  for (const p of ALL_PORTS) {
    const key = `${p.name}|${Math.round(p.lat * 10)}|${Math.round(p.lng * 10)}`;
    if (!seen.has(key)) seen.set(key, p);
  }
  return Array.from(seen.values());
})();

export default function WorldAtlas() {
  const { theme } = useTheme();
  const colourKey = theme === "light" ? "lightColor" : "color";
  const [layers, setLayers] = useState({
    navy: true,
    business: true,
    leisure: true,
    cruises: true,
  });
  const [cinema, setCinema] = useState(false);
  const mapRef = useRef(null);

  const toggle = (key) =>
    setLayers((s) => ({ ...s, [key]: !s[key] }));

  // Combine all visible points (each tagged with its layer)
  const points = useMemo(() => {
    const out = [];
    if (layers.navy) {
      for (const p of NAVY_UNIQUE_PORTS) {
        out.push({
          ...p,
          layer: "navy",
          color: LAYERS.navy[colourKey],
          context: `${p.ship} — ${p.dates}`,
        });
      }
    }
    if (layers.business) {
      for (const p of BUSINESS_PLACES) {
        out.push({
          ...p,
          layer: "business",
          color: LAYERS.business[colourKey],
          context: "Information Management Consultant",
        });
      }
    }
    if (layers.leisure) {
      for (const p of LEISURE_PLACES) {
        out.push({
          ...p,
          layer: "leisure",
          color: LAYERS.leisure[colourKey],
          context: "Family holiday",
        });
      }
    }
    if (layers.cruises) {
      for (const p of CRUISE_PORTS) {
        out.push({
          ...p,
          layer: "cruises",
          color: LAYERS.cruises[colourKey],
          context: `${p.ship} — ${p.dates}`,
        });
      }
    }
    return out;
  }, [layers, colourKey]);

  // Navy ship voyage lines + cruise lines (when their layers are on)
  const navyLines = useMemo(() => {
    if (!layers.navy) return [];
    return VOYAGES.map((v) => ({
      slug: v.slug,
      positions: v.ports.map((p) => [p.lat, p.lng]),
      color: LAYERS.navy[colourKey],
      kind: "navy",
    }));
  }, [layers.navy, colourKey]);

  const cruiseLines = useMemo(() => {
    if (!layers.cruises) return [];
    return CRUISES
      // Don't draw a route line for cruises whose port list we couldn't verify
      .filter((c) => !c.portsUnknown)
      .map((c) => ({
        slug: c.id,
        positions: c.ports.map((p) => [p.lat, p.lng]),
        color: LAYERS.cruises[colourKey],
        kind: "cruise",
      }));
  }, [layers.cruises, colourKey]);

  const allLines = useMemo(() => [...navyLines, ...cruiseLines], [navyLines, cruiseLines]);

  // Re-frame the map when filters change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || points.length === 0) return;
    const bounds = points.map((p) => [p.lat, p.lng]);
    try {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 4 });
    } catch (_) {
      /* noop */
    }
  }, [points]);

  // Free scroll/pan in normal mode; lock while the cinematic drives the camera
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (cinema) {
      map.scrollWheelZoom.disable();
      map.dragging.disable();
    } else {
      map.scrollWheelZoom.enable();
      map.dragging.enable();
    }
  }, [cinema]);

  const totalUnique = useMemo(() => {
    const set = new Set();
    for (const p of points) set.add(p.name);
    return set.size;
  }, [points]);

  return (
    <section
      id="atlas"
      className="relative z-10 pb-20 sm:pb-28 md:pb-32 bg-[var(--c-bg)] scroll-mt-16 sm:scroll-mt-20"
      data-testid="atlas-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-16">
        {/* Layer toggles */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-5 sm:mb-6">
          <LayerChip
            active={layers.navy}
            onClick={() => toggle("navy")}
            label="Navy"
            sub="11 ships · 1962–78"
            count={NAVY_UNIQUE_PORTS.length}
            color={LAYERS.navy[colourKey]}
            testid="atlas-toggle-navy"
          />
          <LayerChip
            active={layers.business}
            onClick={() => toggle("business")}
            label="Business"
            sub="Consultant · 1978–"
            count={BUSINESS_COUNT_UNIQUE}
            color={LAYERS.business[colourKey]}
            testid="atlas-toggle-business"
          />
          <LayerChip
            active={layers.leisure}
            onClick={() => toggle("leisure")}
            label="Leisure"
            sub="Family holidays"
            count={LEISURE_COUNT_UNIQUE}
            color={LAYERS.leisure[colourKey]}
            testid="atlas-toggle-leisure"
          />
          <LayerChip
            active={layers.cruises}
            onClick={() => toggle("cruises")}
            label="Cruises"
            sub="Royal Caribbean · 2010–25"
            count={CRUISE_PORTS_UNIQUE}
            color={LAYERS.cruises[colourKey]}
            testid="atlas-toggle-cruises"
          />
          <span className="ml-auto font-mono-plex text-[10px] sm:text-xs text-[var(--c-text-muted)] uppercase tracking-[0.18em]">
            <span className="text-[var(--c-accent)]" data-testid="atlas-total">
              {totalUnique}
            </span>{" "}
            places shown
          </span>
        </div>

        {/* Map */}
        <div
          className="atlas-vintage relative w-full h-[460px] sm:h-[560px] md:h-[640px] border border-[var(--c-border)] bg-[var(--c-bg-elev)] overflow-hidden"
          data-testid="atlas-map-wrapper"
        >
          {!cinema && (
            <button
              onClick={() => {
                const map = mapRef.current;
                if (!map || points.length === 0) return;
                try {
                  map.fitBounds(
                    points.map((p) => [p.lat, p.lng]),
                    { padding: [40, 40], maxZoom: 4 }
                  );
                } catch (_) {
                  /* noop */
                }
              }}
              className="absolute top-3 right-3 z-[1000] w-9 h-9 flex items-center justify-center border border-[var(--c-border)] bg-themed-90 backdrop-blur-xl text-[var(--c-text)] hover:text-[var(--c-accent)] hover:border-[var(--c-accent)] transition-colors"
              title="Reset view to fit all places"
              aria-label="Reset map view"
              data-testid="atlas-reset-view-btn"
            >
              <Maximize2 size={14} />
            </button>
          )}
          <MapContainer
            center={[20, 10]}
            zoom={2}
            zoomSnap={0.25}
            fadeAnimation={false}
            scrollWheelZoom={true}
            worldCopyJump
            ref={mapRef}
            className="w-full h-full"
            data-testid="atlas-map"
            style={{ background: "var(--c-bg-elev-2)" }}
          >
            <TileLayer
              key={theme}
              attribution={TILES[theme].attribution}
              url={TILES[theme].url}
              subdomains="abcd"
              maxZoom={19}
            />

            {/* Voyage / cruise track lines */}
            {!cinema &&
              allLines.map((l) => (
                <Polyline
                  key={l.kind + "-" + l.slug + "-line"}
                  positions={l.positions}
                  pathOptions={{
                    color: l.color,
                    weight: 1.1,
                    opacity: 0.45,
                    dashArray: "4 6",
                  }}
                />
              ))}

            {!cinema &&
              points.map((p, i) => (
              <Marker
                key={`${p.layer}-${p.name}-${i}`}
                position={[p.lat, p.lng]}
                icon={pinIcon(p.layer, p.color)}
              >
                <Tooltip
                  direction="top"
                  offset={[0, -10]}
                  opacity={1}
                  className="voyage-tip"
                >
                  {p.name}
                </Tooltip>
                <Popup>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] font-mono text-[var(--c-accent)]">
                      {LAYERS[p.layer].label}
                    </p>
                    <p className="font-serif text-lg mt-1 text-[var(--c-text)]">
                      {p.name}
                      {p.country && (
                        <span className="text-[var(--c-text-muted)] text-sm italic">
                          {" "}· {p.country}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-[var(--c-text-muted)] mt-2 italic">
                      {p.context}
                    </p>
                    {p.note && (
                      <p className="text-xs text-[var(--c-text-muted)] mt-2 max-w-[240px]">
                        {p.note}
                      </p>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
          <VoyageCinema mapRef={mapRef} onActiveChange={setCinema} />
        </div>

        {/* Four column place list */}
        <div className="mt-12 sm:mt-16 grid gap-8 sm:gap-10 md:grid-cols-2 lg:grid-cols-4">
          <PlaceColumn
            title="Navy"
            sub="Ships' Ports of Call"
            color={LAYERS.navy[colourKey]}
            places={NAVY_UNIQUE_PORTS.map((p) => ({
              name: p.name,
              country: p.country,
            }))}
            testid="atlas-col-navy"
          />
          <PlaceColumn
            title="Business"
            sub="Consultant Assignments"
            color={LAYERS.business[colourKey]}
            places={dedupePlaces(BUSINESS_PLACES)}
            testid="atlas-col-business"
          />
          <PlaceColumn
            title="Leisure"
            sub="Family Holidays"
            color={LAYERS.leisure[colourKey]}
            places={dedupePlaces(LEISURE_PLACES)}
            testid="atlas-col-leisure"
          />
          <PlaceColumn
            title="Cruises"
            sub="Royal Caribbean Ports"
            color={LAYERS.cruises[colourKey]}
            places={dedupePlaces(CRUISE_PORTS)}
            testid="atlas-col-cruises"
          />
        </div>
      </div>
    </section>
  );
}

function dedupePlaces(arr) {
  const seen = new Set();
  return arr
    .filter((p) => {
      if (seen.has(p.name)) return false;
      seen.add(p.name);
      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

function LayerChip({ active, onClick, label, sub, count, color, testid }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={`glass-chip inline-flex items-center gap-2 sm:gap-3 border px-4 sm:px-5 py-2 sm:py-2.5 font-mono-plex text-[10px] sm:text-xs uppercase tracking-[0.18em] transition-colors min-h-[44px] ${
        active
          ? "border-[var(--c-accent)] bg-[var(--c-accent)]/10 text-[var(--c-text)]"
          : "border-[var(--c-border)] text-[var(--c-text-muted)] hover:border-[var(--c-accent)]/60 hover:text-[var(--c-text)]"
      }`}
      data-testid={testid}
    >
      <span
        className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
        style={{
          backgroundColor: active ? color : "transparent",
          border: `1.5px solid ${color}`,
        }}
      />
      <span className="text-left leading-tight">
        {label}
        {sub && (
          <span className="block text-[9px] text-[var(--c-text-muted)] normal-case tracking-normal mt-0.5">
            {sub}
          </span>
        )}
      </span>
      <span className="ml-1 text-[10px] text-[var(--c-text-muted)]">{count}</span>
    </button>
  );
}

function PlaceColumn({ title, sub, color, places, testid }) {
  return (
    <div
      className="border-l-2 pl-4 sm:pl-5"
      style={{ borderColor: color }}
      data-testid={testid}
    >
      <p className="overline mb-2">{sub}</p>
      <h3 className="font-serif-display text-xl sm:text-2xl text-[var(--c-text)]">
        {title}
      </h3>
      <p className="text-xs text-[var(--c-text-muted)] mt-1 font-mono-plex">
        {places.length} places
      </p>
      <ol
        className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-x-3 gap-y-1 text-sm text-[var(--c-text)] font-serif-display leading-relaxed"
      >
        {places.map((p, i) => (
          <li key={p.name + i} className="flex gap-2">
            <span
              className="font-mono-plex text-[10px] mt-1 shrink-0"
              style={{ color }}
            >
              {String(i + 1).padStart(2, "0")}
            </span>
            <span>
              {p.name}
              {p.country && (
                <span className="text-[var(--c-text-muted)] italic text-xs">
                  {" "}· {p.country}
                </span>
              )}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
